
import { defineComponent, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'

export default defineComponent({
    props: {
        minWidth: {
            type: String,
            default: '1100px'
        }
    },
    setup(props) {
        const container = ref<HTMLElement | null>(null)
        const wrapper = ref<HTMLElement | null>(null)
        const thumb = ref<HTMLElement | null>(null)
        const thumbBottom = ref<HTMLElement | null>(null)
        
        const hasScroll = ref(false)

        let resizeObserver: any | null = null
        let startX = 0
        let startScroll = 0

        const checkScroll = () => {
            if (!container.value) return
            hasScroll.value = container.value.scrollWidth > container.value.clientWidth
        }

        const updateThumbSize = () => {
            if (!container.value || !thumb.value || !thumbBottom.value) return

            const track = thumb.value.parentElement!
            const style = getComputedStyle(track)
            const trackWidth = track.clientWidth 
                - parseFloat(style.paddingLeft) 
                - parseFloat(style.paddingRight)

            const ratio = container.value.clientWidth / container.value.scrollWidth
            const width = Math.max(trackWidth * ratio, 40)

            thumb.value.style.width = `${width}px`
            thumbBottom.value.style.width = `${width}px`
        }

        const syncThumb = () => {
            if (!container.value || !thumb.value || !thumbBottom.value) return

            const c = container.value
            const trackTop = thumb.value.parentElement!
            const trackBottom = thumbBottom.value.parentElement!

            const maxScroll = c.scrollWidth - c.clientWidth
            if (maxScroll === 0) return

            const styleTop = getComputedStyle(trackTop)
            const trackTopPaddingLeft = parseFloat(styleTop.paddingLeft)
            const trackTopPaddingRight = parseFloat(styleTop.paddingRight)
            const trackTopWidth = trackTop.clientWidth - trackTopPaddingLeft - trackTopPaddingRight

            const styleBottom = getComputedStyle(trackBottom)
            const trackBottomPaddingLeft = parseFloat(styleBottom.paddingLeft)
            const trackBottomPaddingRight = parseFloat(styleBottom.paddingRight)
            const trackBottomWidth = trackBottom.clientWidth - trackBottomPaddingLeft - trackBottomPaddingRight

            const thumbWidthTop = thumb.value.offsetWidth
            const thumbWidthBottom = thumbBottom.value.offsetWidth

            const ratio = c.scrollLeft / maxScroll

            thumb.value.style.transform = `translateX(${ratio * (trackTopWidth - thumbWidthTop)}px)`
            thumbBottom.value.style.transform = `translateX(${ratio * (trackBottomWidth - thumbWidthBottom)}px)`
        }

        const onThumbMouseDown = (e: MouseEvent) => {
            e.preventDefault()

            if (!container.value) return

            startX = e.clientX
            startScroll = container.value.scrollLeft

            document.addEventListener('mousemove', onThumbMouseMove)
            document.addEventListener('mouseup', onThumbMouseUp)
        }

        const onThumbMouseMove = (e: MouseEvent) => {
            if (!container.value || !thumb.value) return

            const dx = e.clientX - startX
            const c = container.value

            const track = thumb.value.parentElement!
            const style = getComputedStyle(track)
            const trackWidth = track.clientWidth 
                - parseFloat(style.paddingLeft) 
                - parseFloat(style.paddingRight)

            const thumbWidth = thumb.value.offsetWidth
            const maxScroll = c.scrollWidth - c.clientWidth
            if (trackWidth - thumbWidth === 0) return

            const scrollRatio = maxScroll / (trackWidth - thumbWidth)
            c.scrollLeft = startScroll + dx * scrollRatio

            syncThumb()
        }

        const onThumbMouseUp = () => {
            document.removeEventListener('mousemove', onThumbMouseMove)
            document.removeEventListener('mouseup', onThumbMouseUp)
        }

        const onThumbTouchStart = (e: TouchEvent) => {
            e.preventDefault()
            if (!container.value) return

            startX = e.touches[0].clientX
            startScroll = container.value.scrollLeft

            document.addEventListener('touchmove', onThumbTouchMove, { passive: false })
            document.addEventListener('touchend', onThumbTouchEnd)
        }

        const onThumbTouchMove = (e: TouchEvent) => {
            e.preventDefault()
            if (!container.value || !thumb.value) return

            const dx = e.touches[0].clientX - startX
            const c = container.value

            const track = thumb.value.parentElement!
            const style = getComputedStyle(track)
            const trackWidth = track.clientWidth 
                - parseFloat(style.paddingLeft) 
                - parseFloat(style.paddingRight)

            const thumbWidth = thumb.value.offsetWidth
            const maxScroll = c.scrollWidth - c.clientWidth
            if (trackWidth - thumbWidth === 0) return

            const scrollRatio = maxScroll / (trackWidth - thumbWidth)
            c.scrollLeft = startScroll + dx * scrollRatio

            syncThumb()
        }

        const onThumbTouchEnd = () => {
            document.removeEventListener('touchmove', onThumbTouchMove)
            document.removeEventListener('touchend', onThumbTouchEnd)
        }

        onMounted(async () => {
            await nextTick()
            checkScroll()

            updateThumbSize()

            if (container.value) {
                container.value.addEventListener('scroll', syncThumb)
            }

            // @ts-ignore
            resizeObserver = new ResizeObserver(checkScroll)

            if (wrapper.value) {
                resizeObserver.observe(wrapper.value)
            }

            window.addEventListener('resize', checkScroll)
        })

        onBeforeUnmount(() => {
            window.removeEventListener('resize', checkScroll)
            resizeObserver?.disconnect()

            thumb.value?.removeEventListener('mousedown', onThumbMouseDown)
            thumbBottom.value?.removeEventListener('mousedown', onThumbMouseDown)
        })
        
        watch(hasScroll, async (v) => {
            if (v) {
                await nextTick()

                thumb.value?.addEventListener('mousedown', onThumbMouseDown, { passive: false })
                thumb.value?.addEventListener('touchstart', onThumbTouchStart, { passive: false })
                
                thumbBottom.value?.addEventListener('mousedown', onThumbMouseDown, { passive: false })
                thumbBottom.value?.addEventListener('touchstart', onThumbTouchStart, { passive: false })                

                updateThumbSize()
                syncThumb()
            }
        })

        return {
            container,
            wrapper,
            thumb,
            thumbBottom,
            hasScroll
        }
    }
})
